import {
  isObjectRecord,
  toArrayIndexPathSegment,
  toObjectKeyPathSegment,
  type ObjectRecord,
  type PathSegments
} from "./esim-lookup.ordering.common";
import { MAX_RELAXED_PATH_CANDIDATES, RelaxedPathResolver, resolveMaxRelaxedPathCacheEntries } from "./esim-lookup.ordering.wildcards";

export const DEFAULT_MAX_NODE_VISITS = 100_000;
export const DEFAULT_MAX_OBJECT_KEYS_PER_OBJECT = 1_000;
export const DEFAULT_MAX_RELAXED_PATH_ENTRIES = MAX_RELAXED_PATH_CANDIDATES * 64;

type ProfileTraversalFrame = { node: unknown; pathSegments: PathSegments };

export type ProfileOrderIndexLimits = {
  maxNodeVisits: number;
  maxObjectKeysPerObject: number;
  maxRelaxedPathEntries: number;
  maxRelaxedPathCacheEntries: number;
};

export class OrderingLimitExceededError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "OrderingLimitExceededError";
  }
}

export function resolveMaxNodeVisits(maxNodeVisits: number | undefined): number {
  if (typeof maxNodeVisits !== "number" || !Number.isFinite(maxNodeVisits) || maxNodeVisits < 1) {
    return DEFAULT_MAX_NODE_VISITS;
  }

  return Math.floor(maxNodeVisits);
}

export function resolveMaxObjectKeysPerObject(maxObjectKeysPerObject: number | undefined): number {
  if (typeof maxObjectKeysPerObject !== "number" || !Number.isFinite(maxObjectKeysPerObject) || maxObjectKeysPerObject < 1) {
    return DEFAULT_MAX_OBJECT_KEYS_PER_OBJECT;
  }

  return Math.floor(maxObjectKeysPerObject);
}

export function resolveMaxRelaxedPathEntries(maxRelaxedPathEntries: number | undefined): number {
  if (typeof maxRelaxedPathEntries !== "number" || !Number.isFinite(maxRelaxedPathEntries) || maxRelaxedPathEntries < 1) {
    return DEFAULT_MAX_RELAXED_PATH_ENTRIES;
  }

  return Math.floor(maxRelaxedPathEntries);
}

export class ProfileOrderIndex {
  private readonly orderByExactPath = new Map<string, number>();
  private readonly orderByRelaxedPath = new Map<string, number>();
  private nodeVisits = 0;
  private relaxedPathEntryCount = 0;

  private constructor(
    private readonly pathResolver: RelaxedPathResolver,
    private readonly maxNodeVisits: number,
    private readonly maxObjectKeysPerObject: number,
    private readonly maxRelaxedPathEntries: number
  ) {}

  public static fromProfile(profileJson: unknown, limits: ProfileOrderIndexLimits): ProfileOrderIndex {
    const profileOrderIndex = new ProfileOrderIndex(
      new RelaxedPathResolver(resolveMaxRelaxedPathCacheEntries(limits.maxRelaxedPathCacheEntries)),
      limits.maxNodeVisits,
      limits.maxObjectKeysPerObject,
      limits.maxRelaxedPathEntries
    );
    profileOrderIndex.collectKeyOrders(profileJson, []);
    return profileOrderIndex;
  }

  public resolveOrderIndex(pathSegments: PathSegments): number | undefined {
    const exactIndex = this.orderByExactPath.get(this.pathResolver.toPath(pathSegments));
    if (exactIndex !== undefined) {
      return exactIndex;
    }

    for (const candidatePath of this.pathResolver.getRelaxedPathCandidates(pathSegments)) {
      const relaxedIndex = this.orderByRelaxedPath.get(candidatePath);
      if (relaxedIndex !== undefined) {
        return relaxedIndex;
      }
    }

    return undefined;
  }

  private collectKeyOrders(node: unknown, pathSegments: PathSegments): void {
    const stack: ProfileTraversalFrame[] = [{ node, pathSegments }];

    while (stack.length > 0) {
      this.assertNodeVisitCapacity();
      this.nodeVisits += 1;

      const frame = stack.pop();
      if (!frame) {
        continue;
      }

      if (Array.isArray(frame.node)) {
        for (let index = frame.node.length - 1; index >= 0; index -= 1) {
          this.assertStackCapacity(stack.length);
          stack.push({
            node: frame.node[index],
            pathSegments: [...frame.pathSegments, toArrayIndexPathSegment(index)]
          });
        }
        continue;
      }

      if (!isObjectRecord(frame.node)) {
        continue;
      }

      this.collectObjectKeyOrders(frame.node, frame.pathSegments, stack);
    }
  }

  private collectObjectKeyOrders(node: ObjectRecord, pathSegments: PathSegments, stack: ProfileTraversalFrame[]): void {
    const keys = Object.keys(node);
    this.assertObjectKeysPerObjectCapacity(keys.length);

    const keyPathSegmentsByIndex: PathSegments[] = new Array(keys.length);
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      const keyPathSegments = [...pathSegments, toObjectKeyPathSegment(key)];
      keyPathSegmentsByIndex[index] = keyPathSegments;

      this.orderByExactPath.set(this.pathResolver.toPath(keyPathSegments), index);
      this.addRelaxedCandidates(keyPathSegments, index);
    }

    for (let index = keys.length - 1; index >= 0; index -= 1) {
      const key = keys[index];
      this.assertStackCapacity(stack.length);
      stack.push({
        node: node[key],
        pathSegments: keyPathSegmentsByIndex[index]
      });
    }
  }

  private addRelaxedCandidates(pathSegments: PathSegments, index: number): void {
    for (const candidatePath of this.pathResolver.getRelaxedPathCandidates(pathSegments)) {
      if (this.orderByRelaxedPath.has(candidatePath)) {
        continue;
      }

      this.assertRelaxedPathEntryCapacity();
      this.orderByRelaxedPath.set(candidatePath, index);
      this.relaxedPathEntryCount += 1;
    }
  }

  private assertRelaxedPathEntryCapacity(): void {
    if (this.relaxedPathEntryCount >= this.maxRelaxedPathEntries) {
      throw new OrderingLimitExceededError(
        `Exceeded maximum relaxed path entries (${this.maxRelaxedPathEntries}) while collecting profile key order.`
      );
    }
  }

  private assertNodeVisitCapacity(): void {
    if (this.nodeVisits >= this.maxNodeVisits) {
      throw new OrderingLimitExceededError(`Exceeded maximum node visits (${this.maxNodeVisits}) while collecting profile key order.`);
    }
  }

  private assertStackCapacity(stackLength: number): void {
    if (stackLength >= this.maxNodeVisits) {
      throw new OrderingLimitExceededError(`Exceeded maximum queued nodes (${this.maxNodeVisits}) while collecting profile key order.`);
    }
  }

  private assertObjectKeysPerObjectCapacity(objectKeyCount: number): void {
    if (objectKeyCount > this.maxObjectKeysPerObject) {
      throw new OrderingLimitExceededError(
        `Exceeded maximum object keys per object (${this.maxObjectKeysPerObject}) while collecting profile key order.`
      );
    }
  }
}
